import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Uploads the selected file straight to storage (S3 / disk), then stores the
// signed blob id in a hidden field so the form submit never carries file bytes.
export default class extends Controller {
  static targets = ["input", "hidden", "progress", "progressBar", "status", "error"]
  static values = {
    url: String,
    acceptExtensions: String
  }

  upload(event) {
    const file = event.target.files?.[0]
    this.clearError()

    if (!file) {
      this.resetHidden()
      this.hideProgress()
      return
    }

    if (!this.isAllowedExtension(file.name)) {
      this.showError("That file type is not supported.")
      this.inputTarget.value = ""
      this.resetHidden()
      this.hideProgress()
      return
    }

    this.showProgress(0)
    this.setStatus("Uploading…")
    this.disableSubmit(true)

    const upload = new DirectUpload(file, this.urlValue, this)
    upload.create((error, blob) => {
      this.disableSubmit(false)

      if (error || !blob) {
        this.showError("We could not upload that file. Please try again.")
        this.inputTarget.value = ""
        this.resetHidden()
        this.hideProgress()
        return
      }

      this.hiddenTarget.disabled = false
      this.hiddenTarget.value = blob.signed_id
      this.showProgress(100)
      this.setStatus(`Uploaded ${file.name}`)
      // Clear the file input so the form does not try to multipart-upload again.
      this.inputTarget.value = ""
    })
  }

  // Called by @rails/activestorage DirectUpload while PUTting to storage.
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return
      const progress = Math.round((event.loaded / event.total) * 100)
      this.showProgress(progress)
      this.setStatus(`Uploading… ${progress}%`)
    })
  }

  isAllowedExtension(filename) {
    if (!this.hasAcceptExtensionsValue || !this.acceptExtensionsValue) return true

    const allowed = this.acceptExtensionsValue
      .split(",")
      .map((ext) => ext.trim().toLowerCase())
      .filter(Boolean)
    if (allowed.length === 0) return true

    const lower = filename.toLowerCase()
    return allowed.some((ext) => lower.endsWith(ext.startsWith(".") ? ext : `.${ext}`))
  }

  showProgress(percent) {
    if (this.hasProgressTarget) this.progressTarget.classList.remove("hidden")
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${percent}%`
  }

  hideProgress() {
    if (this.hasProgressTarget) this.progressTarget.classList.add("hidden")
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = "0%"
    this.setStatus("")
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  showError(message) {
    if (!this.hasErrorTarget) {
      window.alert(message)
      return
    }
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  resetHidden() {
    if (!this.hasHiddenTarget) return
    this.hiddenTarget.value = ""
    this.hiddenTarget.disabled = true
  }

  disableSubmit(disabled) {
    const form = this.element.closest("form")
    if (!form) return

    form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((button) => {
      button.disabled = disabled
    })
  }
}
